import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AiOutlineClose, AiOutlineMenu, AiOutlineUser } from 'react-icons/ai';
import { FaPlus } from 'react-icons/fa6';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import Logo from '../assets/Logo.svg';
import { ROUTES } from '../constants/routes';
import { logoutRequested } from '../redux/slices/authSlice';
import { t } from 'i18next';
import { LanguageSwitcher } from './LanguageSwitcher';

const NAV_LINKS = [
  { to: ROUTES.HOME, labelKey: 'navbar.links.homeAlt' },
  { to: ROUTES.ABOUT_US, labelKey: 'navbar.links.aboutSLI' },
  { to: ROUTES.TEMP_FARM_SHARE, labelKey: 'navbar.links.farmshare' },
  { to: ROUTES.EVENTS, labelKey: 'navbar.links.events' },
  { to: ROUTES.RESOURCES, labelKey: 'navbar.links.resources' },
];

const Navbar = ({ src, pageName, pageText }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const user = useSelector((state) => state.auth.user);
  const navigate = useNavigate();
  const [isShowingSidenav, setIsShowingSidenav] = useState(false);
  const [isShowingDropdown, setIsShowingDropdown] = useState(false);
  const [mobileDropdown, setMobileDropdown] = useState(false);
  const [isShowingLanguageDropdown, setIsShowingLanguageDropdown] = useState(false);

  const toggleSidenav = () => {
    setIsShowingSidenav((prev) => {
      if (!prev) {
        setMobileDropdown(false);
      }
      return !prev;
    });
  };

  const handleAccount = () => {
    setIsShowingDropdown(!isShowingDropdown);
    setIsShowingLanguageDropdown(false);
  };

  const handleLanguageToggle = () => {
    setIsShowingLanguageDropdown(!isShowingLanguageDropdown);
    setIsShowingDropdown(false);
  };

  const handleMobileAccount = () => {
    setMobileDropdown((prev) => {
      if (!prev) {
        setIsShowingSidenav(false);
      }
      return !prev;
    });
  };

  const handleLogout = async (e) => {
    e.preventDefault();
    setIsShowingDropdown(false);
    setMobileDropdown(false);
    setIsShowingSidenav(false);
    setIsShowingLanguageDropdown(false);
    dispatch(logoutRequested());
    navigate(ROUTES.HOME, { replace: true });
  };

  return (
    <div className="relative w-full">
      <ToastContainer />
      <NavbarPageTitle pageName={pageName} pageText={pageText} />
      <NavbarBackground src={src} />
      <div
        className={`relative w-full flex min-h-[350px] md:min-h-[400px] lg:min-h-[500px] 3xl:text-2xl z-20`}
      >
        <NavbarDesktopLinks />

        <RightNavGroup
          isAuthenticated={isAuthenticated}
          handleAccount={handleAccount}
          isShowingDropdown={isShowingDropdown}
          user={user}
          handleLogout={handleLogout}
        />
        <MobileLogo />
        <MobileSidenavToggle
          handleMobileAccount={handleMobileAccount}
          toggleSidenav={toggleSidenav}
          isAuthenticated={isAuthenticated}
        />
        <MobileSidenav
          isOpen={isShowingSidenav}
          onClose={() => setIsShowingSidenav(false)}
          handleLanguageToggle={handleLanguageToggle}
          isShowingLanguageDropdown={isShowingLanguageDropdown}
        />
        {isAuthenticated && (
          <MobileAccountMenu
            isOpen={mobileDropdown}
            onClose={() => setMobileDropdown(false)}
            user={user}
            handleLogout={handleLogout}
          />
        )}
      </div>
    </div>
  );
};

const MobileLogo = () => {
  const { t } = useTranslation();
  return (
    <div className="md:hidden absolute left-7 top-6 z-30">
      <Link to={ROUTES.HOME}>
        <img className="w-16 h-auto" src={Logo} alt={t('navbar.logo.homeAlt')} />
      </Link>
    </div>
  );
};

const MobileSidenavToggle = ({ handleMobileAccount, toggleSidenav, isAuthenticated }) => {
  const { t } = useTranslation();
  return (
    <div className="md:hidden absolute right-7 md:right-14 top-8 flex gap-2 z-30">
      {isAuthenticated ? (
        <div
          onClick={handleMobileAccount}
          className="text-white text-SLIWhite rounded cursor-pointer border border-green-900 shadow px-2 py-1 text-sm font-semibold flex items-center gap-1"
        >
          <AiOutlineUser className="text-SLIWhite" size={14} />
          <span>{t('navbar.actions.myAccount')}</span>
        </div>
      ) : (
        <a
          href="https://usltrcd-prod-mobile.f2-cloud.com/SelfService/submission/submit/Intake_form"
          target="_blank"
          rel="noreferrer"
          className="text-white text-SLIWhite rounded cursor-pointer border border-green-900 shadow px-2 py-1 text-sm font-semibold flex items-center gap-1"
        >
          <FaPlus className="text-SLIWhite" size={14} />
          <span>{t('navbar.actions.enrollNow')}</span>
        </a>
      )}
      <div
        onClick={toggleSidenav}
        className="md:hidden text-white text-SLIWhite text-center rounded cursor-pointer border border-green-900 shadow"
      >
        <AiOutlineMenu className="p-1" size={30} />
      </div>
    </div>
  );
};

const MobileSidenav = ({ isOpen, onClose, handleLanguageToggle, isShowingLanguageDropdown }) => {
  const { t } = useTranslation();
  const basePanelClasses =
    'fixed top-0 left-0 w-[60%] max-w-xs h-full bg-HeaderGreen text-SLIWhite shadow-lg border-r border-r-[#A8C356] ease-in-out duration-500 z-30 md:hidden flex flex-col';

  return (
    <>
      <div
        className={`md:hidden ${isOpen ? 'fixed inset-0 bg-black/60 z-20' : 'hidden'}`}
        onClick={onClose}
      />
      <aside className={`${basePanelClasses} ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <button
          type="button"
          onClick={onClose}
          className="cursor-pointer absolute right-4 top-4 text-SLIWhite"
          aria-label="Close navigation"
        >
          <AiOutlineClose size={24} />
        </button>
        <nav className="flex-1 overflow-y-auto pt-20">
          <ul>
            {NAV_LINKS.map((link) => (
              <MobileNavLink
                key={link.to}
                to={link.to}
                onNavigate={onClose}
                labelKey={link.labelKey}
              />
            ))}
          </ul>
        </nav>
        <MobileLanguageSwitcher
          isShowingDropdown={isShowingLanguageDropdown}
          handleToggle={handleLanguageToggle}
        />
        <div className="px-6 py-4 border-t border-[#2E7A32]">
          <a
            href="https://usltrcd-prod-mobile.f2-cloud.com/SelfService/submission/submit/Intake_form"
            target="_blank"
            rel="noreferrer"
            onClick={onClose}
            className="block py-3 text-center font-semibold text-SLIWhite bg-SLIGreen rounded shadow"
          >
            {t('navbar.actions.enrollNow')}
          </a>
        </div>
      </aside>
    </>
  );
};

const MobileNavLink = ({ to, onNavigate, labelKey }) => {
  const { t } = useTranslation();
  return (
    <Link to={to} onClick={onNavigate}>
      <li className="px-6 py-4 border-b border-[#2E7A32]">{t(labelKey)}</li>
    </Link>
  );
};

const MobileAccountMenu = ({ isOpen, onClose, user, handleLogout }) => (
  <>
    <div
      className={`md:hidden ${isOpen ? 'fixed inset-0 bg-black/60 z-20' : 'hidden'}`}
      onClick={onClose}
    />
    <aside
      className={`fixed top-0 right-0 w-[60%] max-w-xs h-full bg-HeaderGreen text-SLIWhite shadow-lg border-l border-l-[#A8C356] ease-in-out duration-500 z-30 md:hidden flex flex-col ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}
    >
      <button
        type="button"
        onClick={onClose}
        className="cursor-pointer absolute right-4 top-4 text-SLIWhite"
        aria-label="Close account menu"
      >
        <AiOutlineClose size={24} />
      </button>
      <div className="pt-6 pb-4 px-9 text-lg font-semibold">
        {user?.firstName ? `Hi, ${user.firstName}` : 'My Account'}
      </div>
      <nav className="flex-1 overflow-y-auto px-2">
        <AccountMenuLinks user={user} handleLogout={handleLogout} onNavigate={onClose} />
      </nav>
    </aside>
  </>
);

const EnrollButton = () => {
  const { t } = useTranslation();
  return (
    <div className="h-20 hide-on-mobile-else-flex items-center">
      <a
        className="flex-1 flex items-center"
        href="https://usltrcd-prod-mobile.f2-cloud.com/SelfService/submission/submit/Intake_form"
        target="_blank"
        rel="noreferrer"
      >
        <ul className="navbar-text">
          <li className="navbar-top-right-button w-auto flex items-center justify-center shadow">
            <FaPlus className="text-SLIGreen" size={16} />
            <span className="ml-2">{t('navbar.actions.enrollNow')}</span>
          </li>
        </ul>
      </a>
    </div>
  );
};

const AccountDropdown = ({ user, handleLogout }) => (
  <div className="bg-SLIWhite text-SLIGreen rounded-lg h-auto w-full absolute top-[110%] right-0">
    <div className="flex flex-col">
      <AccountMenuLinks user={user} handleLogout={handleLogout} />
    </div>
  </div>
);

const AccountButton = ({ handleAccount, isShowingDropdown, user, handleLogout }) => (
  <div className="h-20 hide-on-mobile-else-flex items-center cursor-pointer">
    <div
      onClick={handleAccount}
      className="navbar-top-right-button navbar-text select-none relative"
    >
      {t('navbar.actions.myAccount')}
      {isShowingDropdown && <AccountDropdown user={user} handleLogout={handleLogout} />}
    </div>
  </div>
);

const RightNavGroup = ({
  isAuthenticated,
  handleAccount,
  isShowingDropdown,
  user,
  handleLogout,
}) => (
  <div className="h-20 hide-on-mobile-else-flex items-center w-full mt-8 ml-auto pr-[2vw]">
    <ul className="flex items-center gap-6 justify-end navbar-text w-full">
      <li>
        {isAuthenticated ? (
          <AccountButton
            handleAccount={handleAccount}
            isShowingDropdown={isShowingDropdown}
            user={user}
            handleLogout={handleLogout}
          />
        ) : (
          <EnrollButton />
        )}
      </li>
      <li className="hidden md:block">
        <LanguageSwitcher />
      </li>
    </ul>
  </div>
);

const AccountMenuLinks = ({ user, handleLogout, onNavigate }) => {
  const handleLogoutClick = (event) => {
    handleLogout(event);
    if (onNavigate) {
      onNavigate();
    }
  };

  return (
    <>
      <Link
        to={ROUTES.PROFILE}
        className="px-4 py-2 text-sm hover:bg-lime-700 select-none"
        onClick={onNavigate}
      >
        {t('navbar.actions.viewProfile')}
      </Link>
      <hr />
      <Link
        to={ROUTES.MY_RESERVATIONS}
        className="px-4 py-2 text-sm hover:bg-lime-700 select-none"
        onClick={onNavigate}
      >
        {t('navbar.actions.yourReservations')}
      </Link>
      <hr />
      {user?.role === 'admin' && (
        <>
          <Link
            to={ROUTES.USER_LIST}
            className="px-4 py-2 text-sm hover:bg-lime-700 select-none"
            onClick={onNavigate}
          >
            {t('navbar.actions.viewAllUsers')}
          </Link>
          <hr />
          <Link
            to={ROUTES.RESERVATION_LIST}
            className="px-4 py-2 text-sm hover:bg-lime-700 select-none"
            onClick={onNavigate}
          >
            {t('navbar.actions.viewAllReservations')}
          </Link>
          <hr />
          <Link
            to={ROUTES.EQUIPMENT_LIST}
            className="px-4 py-2 text-sm hover:bg-lime-700 select-none"
            onClick={onNavigate}
          >
            {t('navbar.actions.viewAllEquipment')}
          </Link>
          <hr />
        </>
      )}
      <button
        type="button"
        onClick={handleLogoutClick}
        className="px-4 py-2 text-sm hover:bg-lime-700 rounded-b-lg select-none text-left w-full"
      >
        {t('navbar.actions.logout')}
      </button>
    </>
  );
};

const NavbarDesktopLinks = () => {
  const { t } = useTranslation();

  return (
    <div className="h-20 hide-on-mobile-else-flex w-full max-w-[60vw] xl:max-w-[50rem] navbar-responsive-x-margin mt-8">
      <ul className="flex-1 flex items-center gap-14 navbar-text">
        <Link to={ROUTES.HOME} className="flex">
          <div className="xl:flex justify-center align-center">
            <img className="w-[3rem] h-auto" src={Logo} alt={t('navbar.logo.homeAlt')} />
          </div>
        </Link>
        <Link to={ROUTES.ABOUT_US}>
          <li className="px-1 py-1 lg:py-2  text-SLIWhite flex justify-center">
            {t('navbar.links.aboutSLI')}
          </li>
        </Link>
        <Link to={ROUTES.TEMP_FARM_SHARE}>
          <li className="px-1 py-1 lg:py-2  text-SLIWhite flex justify-center">
            {t('navbar.links.farmshare')}
          </li>
        </Link>
        <Link to={ROUTES.EVENTS}>
          <li className="px-1 py-1 lg:py-2  text-SLIWhite flex justify-center">
            {t('navbar.links.events')}
          </li>
        </Link>
        <Link to={ROUTES.RESOURCES}>
          <li className="px-1 py-1 lg:py-2  text-SLIWhite flex justify-center">
            {t('navbar.links.resources')}
          </li>
        </Link>
      </ul>
    </div>
  );
};

const NavbarBackground = ({ src }) => (
  <>
    <div
      className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url(${src})` }}
    ></div>
    <div className="w-full h-full absolute top-0 left-0 z-10 bg-HeaderGreen" />
  </>
);

const NavbarPageTitle = ({ pageName, pageText }) => (
  <div className="text-SLIWhite text-3xl lg:text-4xl xl:text-5xl font-medium text-shadow-lg max-w-[75vw]  navbar-responsive-x-margin top-[110px] md:top-[200px] lg:top-[250px] absolute z-20">
    {pageName}
    <div className="text-lg font-normal leading-normal md:flex w-auto h-auto mt-3 md:max-w-[75vw]">
      {pageText}
    </div>
  </div>
);

const MobileLanguageSwitcher = ({ isShowingDropdown, handleToggle }) => {
  const { i18n, t } = useTranslation();
  const currentLang = (i18n.language || 'en').split('-')[0];

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Español' },
  ];

  const currentLanguage = languages.find((lang) => lang.code === currentLang) || languages[0];

  return (
    <div className="px-6 py-4 border-t border-[#2E7A32]">
      <div className="relative">
        <button
          onClick={handleToggle}
          className="w-full text-left text-SLIWhite hover:bg-lime-700 rounded flex items-center justify-between py-2 px-2"
          aria-label="Select language"
        >
          <div className="flex items-center gap-3">
            <svg
              className="w-5 h-5 stroke-2 fill-none"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M22 12C22 17.5228 17.5228 22 12 22M22 12C22 6.47715 17.5228 2 12 2M22 12H2M12 22C6.47715 22 2 17.5228 2 12M12 22C14.5013 19.2616 15.9228 15.708 16 12C15.9228 8.29203 14.5013 4.73835 12 2M12 22C9.49872 19.2616 8.07725 15.708 8 12C8.07725 8.29203 9.49872 4.73835 12 2M2 12C2 6.47715 6.47715 2 12 2"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="font-bold">{currentLanguage.name}</span>
          </div>
          <svg
            className={`w-4 h-4 transition-transform duration-200 ${isShowingDropdown ? 'rotate-180' : ''}`}
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M6 9l6 6 6-6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        {isShowingDropdown && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-SLIWhite rounded-lg shadow-lg overflow-hidden z-50">
            {languages.map((language) => (
              <button
                key={language.code}
                className={`w-full px-4 py-2 text-sm font-bold text-left transition-colors duration-200 bg-transparent ${
                  currentLanguage.code === language.code
                    ? 'text-SLIGreen'
                    : 'text-SLIBlack hover:text-gray500 hover:bg-gray300'
                }`}
                onClick={() => {
                  i18n.changeLanguage(language.code);
                  handleToggle();
                }}
              >
                {language.name}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export { Navbar };
